import { type ExternalBuyLink, type ExternalPurchaseSource } from "./product"

export const chapterDripIntervals = ["day", "week", "month"] as const
export type ChapterDripInterval = (typeof chapterDripIntervals)[number]

export type ThinCourse = {
	id: number
	platformId: number
	sortOrder: number
	title: string
	thumbnailFilePath: string | null
	price: { currency: string; amount: number } | null
	isManualOnly: boolean
	externalBuyLink: ExternalBuyLink | null
	isBokaderoMemberRequired: boolean | undefined
	accessTimeMonths: number | null
	membershipIds: number[]
	tags: { id: number; label: string }[]
	shortDescription: string
	pageCount: number
	publishDate: Date | null
	pauseDate: Date | null
}

export type Course = {
	id: number
	platformId: number
	sortOrder: number
	title: string
	thumbnailFilePath: string | null
	price: { currency: string; amount: number } | null
	isManualOnly: boolean
	externalBuyLink: ExternalBuyLink | null
	accessTimeMonths: number | null
	membershipIds: number[]
	isBokaderoMemberRequired: boolean | undefined
	prerequisiteCourseId: number | null
	issueCertificates: boolean
	isFreeNavigation: boolean
	chapterSkippingEnabled: boolean
	hasExam: boolean
	commentsEnabled: boolean
	autoApproveComments: boolean
	totalCommentsUnderReview: number
	tags: { id: number; label: string }[]
	shortDescription: string
	presentation: string | null
	chapterDripInterval: ChapterDripInterval | null
	// When set, chapters release on the calendar from this date for every student instead of from each student's start.
	chapterDripStartDate: Date | null
	chapters: { id: number; title: string; pages: { id: number; title: string }[] }[]
	// The first pages in course order, which viewers without access may open.
	freePreviewPageIds: number[]
	pageCount: number
	sales: number
	enrolledStudents: number
	publishDate: Date | null
	pauseDate: Date | null
}

export type CoursePrerequisite = { id: number; title: string }

export type UserCourseAccess =
	| { status: "paywall"; price: { currency: string; amount: number }; accessTimeMonths: number | null }
	| { status: "paused" | "membership-required" | "bokadero-membership-required" | "manual-only" }
	// The viewer must complete another course first.
	| { status: "prerequisite-required"; prerequisite: CoursePrerequisite }
	| ({
			status: "unlocked"
			pages: { total: number; completed: number }
			progress: number
			firstCompletedPageDate: Date | null
			firstUncompletedPage: {
				id: number
				chapterId: number
			} | null
			resources: { type: "attachment" | "link"; label: string; href: string }[]
			commentsUnderReview: number
	  } & (
			| {
					unlockedCause: "purchased"
					expireDate: Date | null
					order:
						| { type: "stripe" | "paypal"; id: number }
						| { type: "external"; id: number; createdBy: ExternalPurchaseSource }
			  }
			| { unlockedCause: "free" | "membership" | "tester" | "creator" }
	  ))

export type AnonymousCourseAccess =
	| { status: "paywall"; price: { currency: string; amount: number }; accessTimeMonths: number | null }
	| { status: "paused" | "membership-required" | "bokadero-membership-required" | "manual-only" }
	// The viewer must complete another course first.
	| { status: "prerequisite-required"; prerequisite: CoursePrerequisite }
	| {
			status: "unlocked"
			unlockedCause: "free"
			pages: { total: number; completed: 0 }
			progress: 0
			firstUncompletedPage: null
			resources: { type: "attachment" | "link"; label: string; href: string }[]
			commentsUnderReview: 0
	  }

export type CourseAccess = AnonymousCourseAccess | UserCourseAccess

export type PersonalizedThinCourse = {
	id: number
	sortOrder: number
	title: string
	thumbnailFilePath: string | null
	shortDescription: string
	price: { currency: string; amount: number } | null
	isManualOnly: boolean
	externalBuyLink: ExternalBuyLink | null
	membershipIds: number[]
	tags: { id: number; label: string }[]
	pageCount: number
} & CourseAccess

export type PersonalizedCourse = {
	id: number
	sortOrder: number
	title: string
	thumbnailFilePath: string | null
	price: { currency: string; amount: number } | null
	externalBuyLink: ExternalBuyLink | null
	membershipIds: number[]
	issueCertificates: boolean
	isFreeNavigation: boolean
	chapterSkippingEnabled: boolean
	hasExam: boolean
	commentsEnabled: boolean
	tags: { id: number; label: string }[]
	shortDescription: string
	presentationHtml: string | null
	chapters: { id: number; title: string; pageIds: number[] }[]
	// The first pages in course order, which viewers without access may open unless the course is paused.
	freePreviewPageIds: number[]
	pageCount: number
} & CourseAccess

export interface CourseTag {
	id: number
	platformId: number
	label: string
}

export const intendedPageTypes = [
	"text",
	"audio",
	"video",
	"youtube",
	"vimeo",
	"loom",
	"iframe",
	"quiz",
	"input",
	"custom",
] as const
export const coursePageInputTypes = ["single-line", "multi-line"] as const
export type IntendedPageType = (typeof intendedPageTypes)[number]
export type CoursePageInputType = (typeof coursePageInputTypes)[number]
export interface CoursePageAttachment {
	id: number
	label: string
	fileType: string
	url: string
}

export interface CoursePageLink {
	id: number
	label: string
	url: string
}

export type PageVideo =
	| { host: "apiVideo"; apiVideoId: string }
	| { host: "bunny"; bunnyVideoId: string; thumbnailUrl: string | null }

type PageImage = { filePath: string; contain: boolean }
export type DetailedCoursePage = {
	id: number
	order: number
	title: string
	attachments: CoursePageAttachment[]
	links: CoursePageLink[]
	chapter: { id: number; title: string; order: number }
	course: {
		id: number
		platformId: number
		title: string
		chapterDripInterval: ChapterDripInterval | null
		chapterDripStartDate: Date | null
		issueCertificates: boolean
		isFreeNavigation: boolean
	}
} & (
	| { type: "text"; htmlContent: string | null; image: PageImage | null }
	| { type: "input"; htmlContent: string | null; image: PageImage | null; inputType: CoursePageInputType }
	| { type: "video"; htmlContent: string | null; video: PageVideo }
	| { type: "youtube"; htmlContent: string | null; youTubeVideoId: string }
	| { type: "vimeo"; htmlContent: string | null; vimeoVideoId: string }
	| { type: "audio"; htmlContent: string | null; image: PageImage | null; audioUrl: string }
	| {
			type: "quiz"
			htmlContent: string | null
			image: PageImage | null
			quiz: { type: "single-answer" | "multi-answer"; answers: CoursePageAnswer[] }
	  }
	| { type: "loom"; htmlContent: string | null; loomVideoId: string }
	| { type: "iframe"; iframeUrl: string | null }
	| { type: "custom"; htmlContent: string | null }
)

export interface CoursePageAnswer {
	id: number
	text: string
}

export interface CourseTester {
	userId: number
	email: string
	firstName: string
	lastName: string
}

export interface CreateCourseExam {
	passScore: number
	questions: {
		label: string
		points: number
		isPointsPerAnswer: boolean
		options: { label: string; isCorrect: boolean }[]
	}[]
}

export interface CourseExam {
	platformId: number
	course: { id: number; title: string; issueCertificates: boolean }
	id: number
	version: number
	passScore: number
	questions: {
		id: number
		label: string
		points: number
		isPointsPerAnswer: boolean
		options: CourseExamOption[]
	}[]
}

export interface CourseExamOption {
	id: number
	label: string
	isCorrect: boolean
}

export type PersonalizedCourseExam = { course: { id: number; title: string } } & (
	| {
			isCompleted: true
			completion: CalculatedExamCompletion
			exam: {
				id: number
				passScore: number
				certificate: { id: number; createDate: Date } | null
				questions: {
					id: number
					label: string
					options: { id: number; label: string; isCorrect: boolean }[]
				}[]
			}
	  }
	| {
			isCompleted: false
			exam: {
				id: number
				questions: {
					id: number
					label: string
					isSingleChoice: boolean
					options: { id: number; label: string }[]
				}[]
			}
	  }
)

export interface CourseExamCompletion {
	examId: number
	userId: number
	answers: number[]
}

export interface DetailedCourseExamCompletion {
	exam: { id: number; version: number }
	user: { id: number; firstName: string; lastName: string }
	answers: number[]
}

export interface CalculatedExamCompletion {
	answers: { all: number[]; correct: number[] }
	score: { total: number; received: number }
}

export type UserCoursePageInput = {
	id: number
	title: string
	chapter: { id: number; title: string }
	text: string
	createDate: Date
	inputs: { id: number; text: string; createDate: Date }[]
}

export type CourseInputs = {
	chapters: {
		id: number
		title: string
		pages: { id: number; title: string; users: CourseInputsUser[] }[]
	}[]
}

export interface CourseInputsUser {
	id: number
	firstName: string
	lastName: string
	inputs: {
		id: number
		text: string
		createDate: Date
	}[]
}
